import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { usePopoverPosition } from "@/components/use-popover-position";

export type SelectItem = {
  name: string;
  value: string;
  icon?: ReactNode;
  /**
   * Extra text the search matches on but never displays — codes, alternate
   * spellings. Country items pass their ISO-2/ISO-3 codes and aliases here so
   * "gb"/"gbr" find United Kingdom.
   */
  keywords?: string;
};

/** Diacritic-insensitive comparison key, so "curacao" matches "Curaçao". */
function searchKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export type AllSelectorConfig = {
  name?: string;
  preselected?: boolean;
  icon?: ReactNode;
};

/**
 * Selection value. Without `nullSelector` it's always a list; with it, the
 * extra option sets the value to `null` (a state distinct from the `[]` "all").
 */
export type MultiSelectValueProps =
  | { selected: string[]; onChange: (next: string[]) => void; nullSelector?: undefined }
  | {
      selected: string[] | null;
      onChange: (next: string[] | null) => void;
      /** Extra option, listed above `allSelector`, that sets the value to `null`. */
      nullSelector: AllSelectorConfig;
    };

type SearchMultiSelectProps = MultiSelectValueProps & {
  items: SelectItem[];
  placeholder?: string;
  icon?: ReactNode;
  name?: string;
  allSelector?: AllSelectorConfig;
  disabled?: boolean;
};

export function SearchMultiSelect({
  items,
  selected,
  onChange: onChangeProp,
  placeholder = "Search",
  icon,
  name,
  allSelector,
  nullSelector,
  disabled = false,
}: SearchMultiSelectProps) {
  // Safe: `null` is only ever emitted when `nullSelector` is set.
  const onChange = onChangeProp as (next: string[] | null) => void;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [popoverEl, setPopoverEl] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pos = usePopoverPosition({
    anchorEl: containerRef.current,
    popoverEl,
    align: "stretch",
  });

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const inPopover = popoverEl?.contains(e.target as Node);
      const inContainer = containerRef.current?.contains(e.target as Node);
      if (!inPopover && !inContainer) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, popoverEl]);

  const filtered = useMemo(() => {
    const q = searchKey(query.trim());
    if (!q) return items;
    return items.filter((i) => searchKey(`${i.name} ${i.keywords ?? ""}`).includes(q));
  }, [query, items]);

  const selectedValues = selected ?? [];

  function toggle(value: string) {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={[
          "flex items-center gap-1.5 rounded-[4px] px-2 py-1.5",
          disabled ? "cursor-not-allowed bg-grey-50" : "bg-grey-100",
        ].join(" ")}
      >
        {icon ?? <Icon name="search" size={20} className="text-grey-500" />}
        <input
          ref={inputRef}
          type="search"
          name={name}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !disabled && setOpen(true)}
          disabled={disabled}
          className="min-w-0 flex-1 border-0 bg-transparent text-small text-black outline-none placeholder:text-grey-500 disabled:cursor-not-allowed disabled:text-grey-500"
        />
      </div>

      {!disabled &&
        open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={setPopoverEl}
            role="listbox"
            aria-multiselectable="true"
            aria-label={placeholder}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: pos?.width ?? 320,
              maxHeight: "min(320px, calc(100vh - 16px))",
              visibility: pos ? "visible" : "hidden",
            }}
            className="fixed z-[1400] flex flex-col overflow-hidden rounded-[4px] border border-grey-200 bg-white p-2 shadow-soft"
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {nullSelector && (
                <SelectorOption
                  config={nullSelector}
                  isSelected={selected === null}
                  onClick={() => onChange(null)}
                  divider={!allSelector}
                />
              )}
              {allSelector &&
                (() => {
                  const allIsSelected =
                    selected !== null &&
                    selected.length === 0 &&
                    (allSelector.preselected ?? false);
                  return (
                    <>
                      <button
                        type="button"
                        role="option"
                        aria-selected={allIsSelected}
                        onClick={() => onChange([])}
                        className={[
                          "flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left transition-colors",
                          allIsSelected
                            ? "bg-green-500-12 text-green-700 hover:bg-green-100"
                            : "text-grey-900 hover:bg-grey-50",
                        ].join(" ")}
                      >
                        {allSelector.icon && (
                          <span className="flex shrink-0 items-center">{allSelector.icon}</span>
                        )}
                        <span className="flex-1 text-small font-medium">
                          {allSelector.name ?? "All"}
                        </span>
                        {allIsSelected && (
                          <Icon name="check" size={20} className="text-green-700" />
                        )}
                      </button>
                      <hr className="my-1 border-grey-100" />
                    </>
                  );
                })()}
              {filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-small text-grey-500">
                  No results match &ldquo;{query}&rdquo;.
                </p>
              ) : (
                filtered.map((item) => {
                  const isSelected = selectedValues.includes(item.value);
                  return (
                    <button
                      key={item.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => toggle(item.value)}
                      className={[
                        "flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left transition-colors",
                        isSelected
                          ? "bg-green-500-12 text-green-700 hover:bg-green-100"
                          : "text-grey-900 hover:bg-grey-50",
                      ].join(" ")}
                    >
                      {item.icon && <span className="flex shrink-0 items-center">{item.icon}</span>}
                      <span className="flex-1 text-small font-medium">{item.name}</span>
                      {isSelected && <Icon name="check" size={20} className="text-green-700" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function SelectorOption({
  config,
  isSelected,
  onClick,
  divider,
}: {
  config: AllSelectorConfig;
  isSelected: boolean;
  onClick: () => void;
  divider: boolean;
}) {
  return (
    <>
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={onClick}
        className={[
          "flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left transition-colors",
          isSelected
            ? "bg-green-500-12 text-green-700 hover:bg-green-100"
            : "text-grey-900 hover:bg-grey-50",
        ].join(" ")}
      >
        {config.icon && <span className="flex shrink-0 items-center">{config.icon}</span>}
        <span className="flex-1 text-small font-medium">{config.name ?? "All"}</span>
        {isSelected && <Icon name="check" size={20} className="text-green-700" />}
      </button>
      {divider && <hr className="my-1 border-grey-100" />}
    </>
  );
}
